<template>
<div class='bubble-chart-div' :class="{inlineDiv: config.isInline}">


<div class="chart-hover-div" v-if="config.legend">
  <p v-if="activeEp.title==null" class='chart-hover-instructions'>click bubbles for more info</p>
  <p v-else class='chart-hover-text' :style="{color: getColor( activeEp.id )}">
    s{{ activeEp.se }}e{{ activeEp.ep }} | {{ activeEp.title }} | {{ activeEp.rating }} stars  | {{ activeEp.viewers }} million viewers
  </p>
</div>

<!-- bubble-chart -->
<div class='bubble-chart' :class="{inlineDiv: config.isInline}">
  
  <!-- xAxis legend above chart -->
  <svg v-show="config.legend" height="20" :width="width" transform="translate(0,0)">
    <g transform="translate(0, 0)">
      <text 
        height="20" 
        :width="width" 
        :transform="'translate(' + (width*1/8) +',' + 20/2 + ')'"
        fill="white"
        text-anchor="start"
        alignment-baseline="central"
      >
        &lt; fewer viewers
      </text>
    </g>
    <g transform="translate(0, 0)">
      <text 
        height="20" 
        :width="width" 
        :transform="'translate(' + (width*7/8) + ',' + 20/2 + ')'"
        fill="white"
        text-anchor="end"
        alignment-baseline="central"
      >
        more viewers &gt;
      </text>
    </g>
  </svg>

  <!-- chart -->
  <svg :height="fullHeight" :width="fullWidth" >

    <!-- gridlines  -->
    <rect 
      v-show="!config.isInline"
      class='gridline-line' 
      v-for="i in [0, 1, 2, 3, 4, 5]" :key="i" 
      :transform="'translate(0,' + (margins[0]+i*height/6) +')'" 
      height="1" :width="fullWidth" 
      :opacity="config.gridlineOpacity" 
      fill="white" />
    <rect
      v-show="!config.isInline"
      class='gridline-line' 
      :transform="'translate(0,' + (margins[0] + height-1) + ')'"
      height="1" :width="fullWidth" 
      :opacity="config.gridlineOpacity" 
      fill="white" />


      <!-- y-axis labels -->
      <g v-if="config.legend" transform="translate(0, 0)">
        <text 
          v-for="j in [0, 1, 2, 3, 4, 5]" :key="j"
          class='gridline-text no-highlight' 
          :transform="'translate(0,' + (margins[0]+(6-j)*height/6 - 5) + ')'"
          text-anchor="start"
          alignment-baseline="top"
          fill="gray">
            {{ j+4+" stars" }}
        </text>
      </g>

    <!-- bubble -->
    <g :transform="'translate(' + margins[0] + ',' + margins[1] + ')'">

      <circle
        class='episode-bubble'
        id='episode-bubble'

        v-for="(episode, i) in filteredShowData"
        :key="i"

        :cx="x(episode[config.x.name])"
        :cy="y(episode[config.y.name])"
        :r="r(episode[config.r.name])"
        
        :fill="getColor(episode[config.r.name])"

        :opacity="getOpacity(episode)"

        @click="activeEp = activeEp.title === episode.title ? {} : episode"
        cursor="pointer"

      >
      </circle>
    </g>
  </svg>

  <!-- bubble size legend below chart -->
  <svg v-show="config.legend" height="20" :width="width" transform="translate(0, 0)">
      <!-- legend bubbles -->
      <g transform="translate(0, 0)">
        <circle
          v-for="rkey in [0, .25, .5, .75, 1]"
          :key="rkey"

          :cx="width/2 - 40 + 80*rkey"
          :cy="20/2"
          :r="r(rRange[1]*rkey)"
          
          :fill="getColor(rRange[1]*rkey)"
          opacity=".75"
        />
      </g>
      <!-- legend text -->
      <g transform="translate(0, 0)">
        <text
          class='no-highlight'
          height="20"
          :width="width" 
          :transform="'translate(' + (width/2 - 50) + ',' + (20/2) + ')'"
          fill="white"
          text-anchor="end"
          alignment-baseline="central"
        >
          first episode
        </text>
        <text
          class='no-highlight'
          height="20"
          :width="width" 
          :transform="'translate(' + (width/2 + 60) + ',' + (20/2) + ')'"
          fill="white"
          text-anchor="start"
          alignment-baseline="central"
        >
          last episode
        </text>
      </g>
  </svg>

</div>
</div>
</template>


<script>

import reject from "lodash.reject";

import { scaleLinear, scaleSequential } from 'd3-scale';
import { interpolateBuPu } from 'd3-scale-chromatic';

import { myMin, myMax } from '../js/helpers.js';

export default {
  props: {
    showData: Array,
    activeShowName: String,
    config: Object,
    resize: Date
  },
  data() {
    return {
      height: 0,
      width: 0,
      fullHeight: 0,
      fullWidth: 0,
      activeEp: {},
      margins: [10, 10, 10, 10]
    }
  },
  mounted() {
    this.setMargins();
    this.setHeightWidth();
  },
  watch: {
    resize() {
      this.setHeightWidth()
    },
    activeShowName() {
      this.activeEp = {};
    },
    config() {
      this.setMargins();
      this.setHeightWidth();
    }
  },
  computed: {
    filteredShowData() {
      return reject(this.showData, {'viewers': null})
    },
    xRange() {
      const min = Object.prototype.hasOwnProperty.call(this.config.x, 'min') ? this.config.x.min : myMin(this.filteredShowData, this.config.x.name);
      const max = Object.prototype.hasOwnProperty.call(this.config.x, 'max') ? this.config.x.max : myMax(this.filteredShowData, this.config.x.name);
      return [min*.9, max*1.05]
    },
    yRange() {
      const min = Object.prototype.hasOwnProperty.call(this.config.y, 'min') ? this.config.y.min : myMin(this.filteredShowData, this.config.y.name);
      const max = Object.prototype.hasOwnProperty.call(this.config.y, 'max') ? this.config.y.max : myMax(this.filteredShowData, this.config.y.name);
      return [min, max]
    },
    rRange() {
      const min = Object.prototype.hasOwnProperty.call(this.config.r, 'min') ? this.config.r.min : myMin(this.filteredShowData, this.config.r.name);
      const max = Object.prototype.hasOwnProperty.call(this.config.r, 'max') ? this.config.r.max : myMax(this.filteredShowData, this.config.r.name);
      return [min, max]
    },
  },
  methods: {
    setMargins() {
      this.margins = this.config.margins == null ? this.margins : this.config.margins;
    },
    setHeightWidth() {
      const height = Object.prototype.hasOwnProperty.call(this.config.shape, 'height') ? this.config.shape.height : this.$el.clientHeight
      const width = Object.prototype.hasOwnProperty.call(this.config.shape, 'width') ? this.config.shape.width : this.$el.clientWidth
      
      this.fullHeight = height;
      this.fullWidth = width;

      this.height = height - this.margins[0] - this.margins[2];
      this.width = width - this.margins[1] - this.margins[3];
    },
    x(val) {
      var scale = scaleLinear()
        .domain(this.xRange)
        .range([0, this.width]);
      return scale(val)
    },
    y(val) {
      var scale = scaleLinear()
        .domain(this.yRange)
        .range([this.height, 0]);
      return scale(val)
    },
    r(val) {
      var scale = scaleLinear()
        .domain(this.rRange)
        .range([this.config.minBubbleSize, this.config.maxBubbleSize]);
      return scale(val)
    },
    getColor(val) {
      let color = scaleSequential()
        .domain([this.rRange[0], this.rRange[1]*1.25])
        .interpolator(interpolateBuPu)
      return color(val)
    },
    getOpacity(episode) {
      if (episode.title === this.activeEp.title) {
        return 1
      } else if (this.activeEp.title == null) {
        return .5
      } else {
        return .1
      }
    }
  }
}
</script>


<style lang='scss' scoped>

.bubble-background {
  fill: $bg-darkest;
}

</style>
